import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Input, Button } from '@components';
import { useTagManagement } from '@hooks/useTagManagement';
import { Document } from '../types';

interface EditDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; tags: string }) => void;
  document: Document | null;
}

export const EditDocumentModal: React.FC<EditDocumentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  document,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    title: '',
    tags: '',
  });

  const {
    tagList,
    currentTag,
    setCurrentTag,
    handleTagKeyPress,
    removeTag,
    resetTags,
    getTagsString,
    isMaxTagsReached,
  } = useTagManagement({
    maxTags: 5,
    initialTags: document?.tags || '',
  });

  useEffect(() => {
    if (document && isOpen) {
      setFormData({
        title: document.title,
        tags: document.tags || '',
      });
    }
  }, [document, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title: formData.title,
      tags: getTagsString(),
    });
    onClose();
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData({ title: '', tags: '' });
      resetTags();
    }
  }, [isOpen, resetTags]);

  if (!document) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('knowledgeBase.editDocument')}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="p-1 h-8 w-8">
            <span className="material-icons">close</span>
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="edit-document-title"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.documentTitleRequired')}
            </label>
            <Input
              id="edit-document-title"
              type="text"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              placeholder={t('knowledgeBase.enterDocumentTitle')}
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              {t('knowledgeBase.descriptiveNameForDocument')}
            </p>
          </div>

          <div>
            <label
              htmlFor="edit-document-tags"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
            >
              {t('knowledgeBase.tags')}{' '}
              {tagList.length > 0 && (
                <span className="text-xs text-gray-500 font-normal">({tagList.length}/5)</span>
              )}
            </label>
            <div className="space-y-2">
              <Input
                id="edit-document-tags"
                type="text"
                value={currentTag}
                onChange={e => setCurrentTag(e.target.value)}
                onKeyPress={handleTagKeyPress}
                placeholder={
                  isMaxTagsReached
                    ? t('knowledgeBase.maximum5TagsReached')
                    : t('knowledgeBase.enterTagsAndPressEnter')
                }
                disabled={isMaxTagsReached}
                className={isMaxTagsReached ? 'opacity-50 cursor-not-allowed' : ''}
              />
              {tagList.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tagList.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200 text-xs rounded-full"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => removeTag(tag)}
                        className="ml-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                      >
                        <span className="material-icons text-sm">close</span>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {t('knowledgeBase.optionalTagsDescription')}
            </p>
          </div>

          <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('knowledgeBase.cancel')}
            </Button>
            <Button type="submit" variant="gradient-multi" className="flex items-center gap-2">
              <span className="material-icons">save</span>
              {t('knowledgeBase.saveChanges')}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
